import { REASON_CODES, reasonMessage, type BoxEventDto, type ReasonCode, type WriteOffReason } from '@pl/domain';

/** The words for a write-off's reason, in the dialogs that ask for one and in the history that shows it. */
export const WRITE_OFF_WORDS: Record<WriteOffReason, string> = { damaged: 'Damaged', lost: 'Lost', sample: 'Sample', other: 'Other' };

export type FoundReason = 'customer_return' | 'found_here' | 'wrong_site';
export const FOUND_WORDS: Record<FoundReason, string> = { customer_return: 'Customer return', found_here: 'Found here', wrong_site: 'Recorded at wrong site' };

/** The Receive screen's own words for where boxes came from. */
const SOURCE_WORDS: Record<string, string> = { truck: 'Truck / container', stocktake: 'Stocktake', other: 'Other' };

/** Reasons the API gives a box event on its own, with no one choosing them. */
const EVENT_REASON_WORDS: Record<string, string> = { order_cancelled: 'Order cancelled', transfer_cancelled: 'Transfer cancelled', left: 'Left as it is' };

export const EVENT_TITLE: Record<string, string> = {
  'box.received': 'Received',
  'box.picked': 'Picked',
  'box.unpicked': 'Put back',
  'box.shipped': 'Shipped',
  'box.sent': 'Sent on transfer',
  'box.received_transfer': 'Received from transfer',
  'box.written_off': 'Written off',
  'box.found': 'Found / returned',
  'box.voided': 'Sticker voided',
  'label.reprinted': 'Sticker reprinted',
  'scan.rejected': 'Scan rejected',
  'scan.override': 'Override used',
  'discrepancy.resolved': 'Discrepancy resolved',
};

const isReasonCode = (r: string): r is ReasonCode => (REASON_CODES as readonly string[]).includes(r);

/** A value no map knows yet still reads as words, never as `snake_case` or `SHOUTING_CASE`. */
function plain(value: string): string {
  const words = value.replace(/[_.]+/g, ' ').trim().toLowerCase();
  return words ? words[0]!.toUpperCase() + words.slice(1) : value;
}

function noteOf(ev: BoxEventDto): string | null {
  const note = ev.payload?.note;
  return typeof note === 'string' && note.trim() ? note.trim() : null;
}

/**
 * Why this happened, in the words the rest of Office uses. A refused scan says what the phone
 * said, which is stored with the event ("This box is recorded at Maryland"), or else the domain's
 * message for its reason code. A write-off or found says its reason and the note written with it.
 */
export function eventReason(ev: BoxEventDto): string | null {
  const r = ev.reason;
  const note = noteOf(ev);
  switch (ev.type) {
    case 'scan.rejected': {
      const said = ev.payload?.message;
      if (typeof said === 'string' && said.trim()) return said.trim();
      if (!r) return null;
      return isReasonCode(r) ? reasonMessage(r) : plain(r);
    }
    case 'scan.override':
      return r; // what the lead typed
    case 'box.written_off':
    case 'box.found':
    case 'discrepancy.resolved': {
      // A shipment's note already says it: "Shipped on order PH-0003".
      if (ev.type === 'discrepancy.resolved' && r === 'shipped' && note) return note;
      const words = r ? ((WRITE_OFF_WORDS as Record<string, string>)[r] ?? (FOUND_WORDS as Record<string, string>)[r] ?? EVENT_REASON_WORDS[r] ?? plain(r)) : null;
      return [words, note].filter(Boolean).join(' · ') || null;
    }
    default:
      return r ? (EVENT_REASON_WORDS[r] ?? (isReasonCode(r) ? reasonMessage(r) : plain(r))) : note;
  }
}

function deviceLabel(ua: string | null): string | null {
  if (!ua) return null;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua) && /Chrome/i.test(ua)) return 'Chrome on Windows';
  return null;
}

export const eventTitle = (ev: BoxEventDto): string => EVENT_TITLE[ev.type] ?? plain(ev.type);

/** The line under an entry's title. The actor is already in the title, so it isn't repeated here. */
export function eventLine(ev: BoxEventDto): string {
  const parts: (string | null)[] = [deviceLabel(ev.userAgent)];
  // A resolved discrepancy's `source` is where the discrepancy came from (a count, a transfer), not
  // where the box came from, so it is not read as a receive's source.
  const source = ev.type === 'discrepancy.resolved' ? null : ev.payload?.source;
  if (typeof source === 'string' && source) parts.push(SOURCE_WORDS[source] ?? plain(source));
  const reference = ev.payload?.reference;
  if (typeof reference === 'string' && reference) parts.push(reference);
  const reason = eventReason(ev);
  // This event's OWN order (it may differ from the box's current order — a box can be
  // released and re-picked onto something else after this event happened), unless its words name it.
  if (ev.orderRef && !reason?.includes(ev.orderRef)) parts.push(ev.orderRef);
  parts.push(reason);
  return parts.filter(Boolean).join(' · ');
}
